import React, { useMemo, useState } from "react";
import { ResultForm } from "./ResultForm";
import { getValue } from "../../utils/object.utils";
import { Modal, ModalSize } from "../Modal";
import { useUpdateResult } from "../../hooks/resultsHooks/results.hooks";
import { formatDateBasedOnTimezone } from "../../utils/date.utils";
import { STANDARD_DATE_FORMAT } from "../../Constants/date";

// The backend only accepts `publish_date` as a plain "YYYY-MM-DD" string.
// `resultDetail.publishDate` comes straight from the API (can be a full ISO
// datetime like "2026-08-28T00:00:00.000Z"), so it must be normalized before
// it's used as the input value or sent back on save.
const toStandardDate = (date: unknown) =>
  date
    ? formatDateBasedOnTimezone({ date, formatType: STANDARD_DATE_FORMAT })
    : "";

interface Props {
  resultId: number;
  resultDetail: object | null;
  onClose: () => unknown;
  open: boolean;
}

export const EditResultModal = ({
  resultId,
  resultDetail,
  onClose,
  open,
}: Props) => {
  const { mutate: updateResult } = useUpdateResult({
    resultId: resultId,
    onSuccess: () => {
      onClose();
    },
    onError: (err: Error) => {
      console.log(err);
    },
  });

  const [publishDate, setPublishDate] = useState(
    toStandardDate(getValue(resultDetail, "publishDate")),
  );
  const [resultType, setResultType] = useState(
    getValue(resultDetail, "resultType"),
  );
  const [totalAttendance, setTotalAttendance] = useState(
    getValue(resultDetail, "totalAttendance"),
  );

  useMemo(() => {
    const publishDate = toStandardDate(getValue(resultDetail, "publishDate"));
    const resultType = getValue(resultDetail, "resultType");
    const totalAttendance = getValue(resultDetail, "totalAttendance");

    setPublishDate(publishDate);
    setResultType(resultType);
    setTotalAttendance(totalAttendance);
  }, [resultDetail]);

  const handleResultUpdate = () => {
    updateResult({
      resultId: resultId,
      body: {
        // `publishDate` is already normalized to "YYYY-MM-DD" above (either
        // from `toStandardDate` on load, or from the date picker itself), so
        // it can be sent as-is here without risking a second timezone shift.
        publish_date: publishDate,
        days_before_exam: totalAttendance,
      },
    });
  };

  return (
    <Modal
      show={open}
      onClose={onClose}
      modalHeader="Edit result"
      modalSize={ModalSize.sm}
      submissionButton={{
        text: "Save",
        onClick: () => {
          handleResultUpdate();
        },
        isSubmitLoading: false,
      }}
    >
      <div>
        <ResultForm
          isEdit={true}
          publishDate={publishDate}
          setPublishDate={setPublishDate}
          resultType={resultType}
          onResultTypeSelect={(resultType) => {
            setResultType(resultType);
          }}
          totalAttendance={totalAttendance}
          setTotalAttendance={setTotalAttendance}
        />
      </div>
    </Modal>
  );
};
